import { Alignment, Api, CurrencySettings, DisplayType, FieldUpdateEvent, ModelRow, StringUtil } from "@mcleod/core";
import {
    ChangeEvent, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Image, Label, Layout,
    LookupModelSelectionEvent, Table, TableRowDisplayEvent, TextboxProps
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { AssetUtil } from "@mcleod/general";
import { DistanceAlertUtil } from "@mcleod/dispatch/src/DistanceAlertUtil";
import { BillingCommentsQuickInfo } from "../../ar/src/BillingCommentsQuickInfo";
import { IntBillCommentsQuickInfo } from "../../ar/src/IntBillCommentsQuickInfo";
import { ExtBillCommentsQuickInfo } from "../../ar/src/ExtBillCommentsQuickInfo";
import { createCommentModelRows } from "../../ar/src/InvoiceProcessingUtil";
import { EdiBilling } from "./EdiBilling";
import { AutogenLayoutEdiBillingOverview } from "./autogen/AutogenLayoutEdiBillingOverview";
import { setCustomerQuickInfo, setPartnerNameQuickInfo } from "./EdiUtility";
import {  DataDisplayEvent } from "@mcleod/components";
import { ImageName } from "@mcleod/images";

export class EdiBillingOverview extends AutogenLayoutEdiBillingOverview {
    private distanceAlertUtil = new DistanceAlertUtil();
    private isAsset: boolean = false;

    onLoad() {
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            // Enable tax panel if multi-currency is enabled
            this.panelTax.visible = true;
        }
    }

    async sourceEdiBillingDetailsAfterExecution(event) {
        const row = this.mainDataSource?.activeRow;

        if (event.getAction() === DataSourceAction.SEARCH) {
            const customerId = row?.get("customer_id");
            if (customerId != null) {
                await this["sourceCustomer"]?.search({ "id": customerId });
            }

            if (row) {
                row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => this.billingFieldUpdated(event));
                this.labelGSDateCaption.visible = row.get("direction") === "I";
                this.labelGsDateTime.visible = row.get("direction") === "I";
            }
            this.setFieldsByDirection();
        }

        this.isAsset = AssetUtil.isAssetMoveType(row);

        // Set tax component visibility based on multi-currency and customer excise taxable settings
        this.setTaxComponentsVisibility();
    }

    private setFieldsByDirection(): void {
        const direction = this.sourceEdiBillingDetails?.activeRow?.get("direction");
        const isInbound = direction === "I";

        // Set visibility to false for inbound, true for outbound
        this.textboxTransferDate.visible = !isInbound;
        this.textboxBillingDate.visible = !isInbound;
        this.textboxBillingUser.visible = !isInbound;
    }
    billingFieldUpdated(event: FieldUpdateEvent) {
        if ("customer_id" == event.fieldName && StringUtil.isEmptyString(event.newValue))
            this.customerChanged(null);
    }

    async sourceEdiMapErrorAfterExecution(event) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            await this.setEdiInvoiceErrorImages();
        }
    }

    textboxCommodityOnDataDisplay() {
        // Quick info is handled by the built-in quickInfoLayout from the commodity model metadata
    }

    customerChanged(customerData: ModelRow<any>) {
        const sourceCustomer = this["sourceCustomer"];
        if (sourceCustomer) {
            sourceCustomer.data = [];
            if (customerData != null) {
                sourceCustomer.data = [customerData];
                sourceCustomer.setRowIndexWithoutUpdate(0);
            }
            sourceCustomer.displayDataInBoundComponents();
            this.setBridgeCustomerLabel();
            // Update tax component visibility when customer changes
            this.setTaxComponentsVisibility();
        }
    }

    sourceDistanceAlertAfterExecution(event: DataSourceExecutionEvent) {
        // Distance alert handling
    }

    async sourceEdiBillingDetailsOnDisplay(event: TableRowDisplayEvent) {
        const sourceEdiBillingDetails = this["sourceEdiBillingDetails"];
        if (!sourceEdiBillingDetails?.activeRow) return;

        const movementId = sourceEdiBillingDetails.activeRow?.get("movement_id") as string;
        const orderId = sourceEdiBillingDetails.activeRow?.get("order_id") as string;
        const customerId = this.mainDataSource?.activeRow?.get("customer_id") as string;
        const companyId = sourceEdiBillingDetails.activeRow?.get("company_id") as string;
        const pbLtl = sourceEdiBillingDetails.activeRow?.get("is_brokerage_ltl");
        const hasIntercompany = sourceEdiBillingDetails.activeRow?.get("has_intercompany") as boolean;
        const hasICerror = sourceEdiBillingDetails.activeRow?.get("has_ic_error") as boolean;

        // Update panel visibility
        const showPanel = this.updateVisibilityBasedOnFlags(this.mainDataSource?.activeRow);
        await this.setCommentsInfo();
        await this.setNotificationsImage();

        if (showPanel) {
            const panelIcons = this["panelIcons"];
            if (panelIcons) {
                panelIcons.visible = true;
            }
        }

        const layoutOrderDataFusionSummary = this.layoutOrderDataFusionSummary;
        if (orderId && customerId) {
            layoutOrderDataFusionSummary.movementId = movementId;
            layoutOrderDataFusionSummary.orderId = orderId;
            layoutOrderDataFusionSummary.customerId = customerId;
            layoutOrderDataFusionSummary.companyId = companyId;
            layoutOrderDataFusionSummary.hasIntercompany = hasIntercompany;
            layoutOrderDataFusionSummary.hasICError = hasICerror;
            const filter = [
                {
                    movement_id: movementId,
                    order_id: orderId,
                    customer_id: customerId,
                    is_brokerage_ltl: pbLtl
                }
            ];
            const body = JSON.stringify(filter);
            await this.delay(1000);
            Api.search("lme/dispatch/order-datafusion-summary", body).then(response => {
                if (response) {
                    layoutOrderDataFusionSummary.setEdiData(response.data[0]);
                    this.panelDataFusion.visible = layoutOrderDataFusionSummary.displayingData;
                }
            });
        }
        // Tooltip setup for total charge if component exists
        // Update tax component visibility when data is displayed
        this.setTaxComponentsVisibility();
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async showErrorPanel(ySplit: boolean) {
        // Simplified error panel handling
    }

    private updateVisibilityBasedOnFlags(activeRow: ModelRow<any>): boolean {
        let showPanel = false;

        // Check for split bill
        const imageSplitBill = this["imageSplitBill"];
        if (imageSplitBill && (activeRow?.get("is_split_bill") === "Y" || activeRow?.get("is_split_bill") === true)) {
            imageSplitBill.visible = true;
            showPanel = true;
        }

        // Check for rebill
        const imageRebill = this["imageRebill"];
        if (imageRebill && (activeRow?.get("rebilled") === "Y" || activeRow?.get("rebilled") === true)) {
            imageRebill.visible = true;
            showPanel = true;
        }

        return showPanel;
    }

    async setCommentsInfo(): Promise<boolean> {
        const orderId = this.mainDataSource?.activeRow?.get("order_id");
        const customerId = this.mainDataSource?.activeRow?.get("customer_id");
        let rowCount = 0;

        if (orderId && customerId) {
            const response = await Api.search("lme/ar/billing-comments", { order_id: orderId, customer_id: customerId })
            rowCount = response.data[0]["number_of_comments"];
        }

        // Show comments icon if there are comments
        const imageComments = this["imageComments"];
        if (imageComments) {
            if (rowCount > 0) {
                imageComments.visible = true;
                this.setCommentsQuickInfo(imageComments);
                const panelIcons = this["panelIcons"];
                if (panelIcons) {
                    panelIcons.visible = true;
                }
            } else {
                imageComments.visible = false;
            }
        }

        return rowCount !== 0;
    }

    private setCommentsQuickInfo(image: Image) {
        image.tooltipDynamicContent = async () => {
            const commentLayout = await Layout.getLoadedLayout("lme/ar/BillingCommentsQuickInfo") as BillingCommentsQuickInfo;
            await commentLayout.sourceBillingComments.search({
                order_id: this.mainDataSource.activeRow?.get("order_id"),
                customer_id: this.mainDataSource.activeRow?.get("customer_id")
            });

            const intRows = commentLayout.sourceBillingComments.data[0].get("int_bill_comments");
            const extRows = commentLayout.sourceBillingComments.data[0].get("ext_bill_comments");

            if (this.areCommentsEmpty(intRows, extRows)) {
                return null;
            }
            this.populateCommentLayout(intRows, commentLayout.layoutIntBillCommentsQuickInfo as IntBillCommentsQuickInfo);
            this.populateCommentLayout(extRows, commentLayout.layoutExtBillCommentsQuickInfo as ExtBillCommentsQuickInfo);
            return commentLayout;
        };
    }

    private areCommentsEmpty(intRows: any[], extRows: any[]): boolean {
        return intRows.length === 0 && extRows.length === 0;
    }

    private populateCommentLayout(rows: any[], layout: IntBillCommentsQuickInfo | ExtBillCommentsQuickInfo) {
        if (rows.length > 0) {
            let table: Table;
            if (layout instanceof IntBillCommentsQuickInfo) {
                table = layout.tableIntBillComments;
            } else {
                table = layout.tableExtBillComments;
            }
            const modelRows = createCommentModelRows(rows);
            table.dataSource.setRowsAndMode(DataSourceMode.NONE, modelRows);
            layout.visible = true;
        } else {
            layout.visible = false;
        }
    }

    async setNotificationsImage() {
        const exceptions = this.getExceptions();
        const numberOfExceptions = exceptions.length;
        const printed = this.mainDataSource.activeRow?.get("printed") === "Y";
        const orderId = this.mainDataSource.activeRow?.get("order_id");
        if (orderId) {
            const response = await Api.search("lme/ar/imaging-documents-lookup", { row_id: orderId, row_type: "O" });
            const imageMissingImage = this.imageNotifications;

            if (this.hasMissingImages(response)) {
                const { missingImages, missingImageCount } = this.getMissingImagesData(response);
                const totalExceptionCount = numberOfExceptions + missingImageCount + (printed ? 1 : 0);
                imageMissingImage.visible = true;
                this.panelIcons.visible = true;

                const textboxPropsArray = this.buildTextboxPropsArray(missingImages, exceptions, printed, totalExceptionCount);
                this.setImageProperties(imageMissingImage, numberOfExceptions, printed);
                CommonTooltips.setTooltipContentTextboxes(imageMissingImage, () => textboxPropsArray);
            } else if (numberOfExceptions > 0) {
                this.setExceptions(exceptions, printed);
            } else {
                imageMissingImage.visible = false;
            }
        }
    }

    private getExceptions(): string[] {
        const exceptions: string[] = [];
        const totalCharges = this.mainDataSource.activeRow?.get("total_charges");
        if (totalCharges?.amount <= 0) {
            exceptions.push("Invoice total charges are 0.");
        }
        const orderId = this.mainDataSource.activeRow?.get("order_id");
        if (StringUtil.isEmptyString(orderId)) {
            exceptions.push("Order ID is required.");
        }
        return exceptions;
    }

    private hasMissingImages(response: any): boolean {
        return Array.isArray(response.data) && response.data.length > 0 && response.data[0].imaging_permitted &&
            response.data[0].required_image_missing_count > 0;
    }

    private getMissingImagesData(response: any) {
        const missingImageCount = response.data[0].required_image_missing_count;
        const images = response.data[0].images;
        const missingImages = images.filter(image => image.is_missing === "Y");
        return { missingImages, missingImageCount };
    }

    private buildTextboxPropsArray(
        missingImages: any[],
        exceptions: string[],
        printed: boolean,
        totalExceptionCount: any
    ): Partial<TextboxProps>[] {
        const textboxPropsArray: Partial<TextboxProps>[] = [totalExceptionCount];
        let totalIndex = -1;

        missingImages.forEach((image, index) => {
            textboxPropsArray[index] = {
                caption: "Missing Image",
                text: image.description,
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
            totalIndex = index;
        });

        exceptions.forEach((exception, index) => {
            textboxPropsArray[totalIndex + 1] = {
                caption: "Invoice Exception",
                text: exception,
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
            totalIndex++;
        });

        return textboxPropsArray;
    }

    private setImageProperties(image: Image, numberOfExceptions: number, printed: boolean) {
        if (numberOfExceptions > 0) {
            image.name = ImageName.circleError
            image.color = "error.reverse";
        } else if (printed) {
            image.color = "primary.reverse";
        } else {
            image.color = "warning.reverse";
        }
    }

    setExceptions(exceptions: any[], printed: boolean) {
        const totalExceptionCount: any = exceptions.length + (printed ? 1 : 0);
        const textboxPropsArray: Partial<TextboxProps>[] = [totalExceptionCount];
        this.imageNotifications.visible = true;
        this.imageNotifications.name = ImageName.circleError;
        this.imageNotifications.color = "error.reverse";
        this.panelIcons.visible = true;

        exceptions.forEach((exception, index) => {
            textboxPropsArray[index] = {
                caption: "Invoice Exception",
                text: exception,
                displayType: DisplayType.STRING,
                rowBreak: true,
                fillRow: true
            };
        });
        CommonTooltips.setTooltipContentTextboxes(this.imageNotifications, () => textboxPropsArray);
    }

    async setEdiInvoiceErrorImages() {
        const imageInboundDFInvoiceError = this["imageInboundDFInvoiceError"];
        const imageOutboundDFInvoiceError = this["imageOutboundDFInvoiceError"];
        const imageWarning = this["imageInboundWarning"];
        const imageOutboundWarning = this["imageOutboundDFInvoiceWarning"];
        const sourceEdiMapError = this["sourceEdiMapError"];

        if (!sourceEdiMapError || !sourceEdiMapError.data) return;

        // Get the invoice direction from the main data source
        const invoiceDirection = this.mainDataSource?.activeRow?.get("direction");

        // Check if there are any EDI map errors (records are already filtered by the data source)
        const hasMapErrors = sourceEdiMapError.data.length > 0;

        // Check if all errors are warnings
        const hasOnlyWarnings = hasMapErrors &&
                              sourceEdiMapError.data.every(errorRow => errorRow.get("error_type") === "W");

        // Check if there are any actual errors (error_type "E")
        const hasActualErrors = hasMapErrors &&
                              sourceEdiMapError.data.some(errorRow => errorRow.get("error_type") === "E");

        // Debug logging
        console.log("EDI Error Images Debug:", {
            hasMapErrors,
            hasOnlyWarnings,
            hasActualErrors,
            invoiceDirection,
            dataLength: sourceEdiMapError.data.length,
            errorTypes: sourceEdiMapError.data.map(row => row.get("error_type")),
            imageWarningExists: !!imageWarning,
            imageInboundExists: !!imageInboundDFInvoiceError,
            imageOutboundExists: !!imageOutboundDFInvoiceError,
            imageOutboundWarningExists: !!imageOutboundWarning
        });

        let panelIconsNeedsToBeVisible = false;

        // First, hide all error images by default
        if (imageInboundDFInvoiceError) {
            imageInboundDFInvoiceError.visible = false;
        }
        if (imageOutboundDFInvoiceError) {
            imageOutboundDFInvoiceError.visible = false;
        }
        if (imageWarning) {
            imageWarning.visible = false;
        }
        if (imageOutboundWarning) {
            imageOutboundWarning.visible = false;
        }

        // Show appropriate icon based on error types and direction
        if (hasMapErrors) {
            if (hasActualErrors) {
                // There are actual errors (type "E") - show error icons
                if (invoiceDirection === "I" && imageInboundDFInvoiceError) {
                    imageInboundDFInvoiceError.visible = true;
                    panelIconsNeedsToBeVisible = true;
                } else if (invoiceDirection === "O" && imageOutboundDFInvoiceError) {
                    imageOutboundDFInvoiceError.visible = true;
                    panelIconsNeedsToBeVisible = true;
                }
            } else if (hasOnlyWarnings) {
                // Only warnings present - show appropriate warning icon based on direction
                if (invoiceDirection === "I" && imageWarning) {
                    // Inbound warnings - show inbound warning icon
                    imageWarning.visible = true;
                    panelIconsNeedsToBeVisible = true;
                } else if (invoiceDirection === "O" && imageOutboundWarning) {
                    // Outbound warnings - show outbound warning icon
                    imageOutboundWarning.visible = true;
                    panelIconsNeedsToBeVisible = true;
                }
            }
        }

        // Show panel if any EDI invoice errors exist
        if (panelIconsNeedsToBeVisible) {
            const panelIcons = this["panelIcons"];
            if (panelIcons) {
                panelIcons.visible = true;
            }
        }
    }

   afterCustomerExecution() {
        this.setBridgeCustomerLabel();
        this.setTaxComponentsVisibility();
    }

    setBridgeCustomerLabel() {
        const bridgeId = this.sourceCustomer?.activeRow?.get("bridge_id");
        if (bridgeId) {
            this.labelBridgeCustomer.visible = true;
        } else {
            this.labelBridgeCustomer.visible = false;
        }
    }

    textboxCommodityIdOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set("commodity_descr", null);
            this.textboxCommodity.text = null;
        }
    }

    textboxCommodityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const commId = event.selection.get("id");
            this.activeRow.set("commodity", commId);
            const billing = this.owner as EdiBilling;
            billing.textboxCommodityId.text = commId;
            const descr = event.selection.get("descr");
            this.activeRow.set("commodity_descr", descr);
            this.textboxCommodity.text = descr;
        }
    }

    panelStopInfoOnClick(event: ClickEvent) {
        const billing = this.owner as EdiBilling;
        billing.tabRate.scrollIntoView();

    }

    bolOnClick(event: ClickEvent) {
        this.textboxBlnum.enabled = true;
        this.textboxBlnum.printable = false;
    }

    consRefnoOnClick(event: ClickEvent) {
        this.textboxConsigneeRefNo.enabled = true;
        this.textboxConsigneeRefNo.printable = false;
    }

    shipIdOnClick(event: ClickEvent) {
        this.textboxShipmentId.enabled = true;
        this.textboxShipmentId.printable = false;
    }
    displayPartnerName(event: DataDisplayEvent) {
        const partnerNameLabel = <Label>event.target;
         const row = this.sourceEdiBillingDetails.activeRow;
        setPartnerNameQuickInfo(partnerNameLabel, row);
    }

     displayCustomer(event: DataDisplayEvent) {
         const customerNameLabel = <Label>event.target;
         const row = this.sourceEdiBillingDetails.activeRow;
         setCustomerQuickInfo(customerNameLabel, row);
     }

     private setTaxComponentsVisibility() {
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.panelTaxInfo.visible = true;
        } else {
            this.panelTaxInfo.visible = false;
        }
    }

    async sourceOrderOnDisplay() {
    }
}
